import { memo, useEffect, useRef, useState } from "react";
//3rd Party librarues
import PropTypes from "prop-types";

// Components
import Input from '../form/input.jsx';

//Styles
import styles from "./style.module.scss"; // Ensure this CSS file is correctly linked

function MonthPicker({
  value = "",
  onChange = () => {},
  disable = false,
  label = "",
  maxDate = "",
  minDate = "",
  error = false,
  errorMessage = "",
  labelClass = "",
}) {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const pickerRef = useRef(null);

  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (value) {
      const [month, year] = value.split("/").map(Number);
      setSelectedYear(year);
      setSelectedMonth(month - 1);
    }
    setInputValue(value);
  }, [value]);

  const selectMonth = (monthIndex) => {
    const newValue = `${String(monthIndex + 1).padStart(
      2,
      "0"
    )}/${selectedYear}`;
    setInputValue(newValue);
    setSelectedMonth(monthIndex);
    setShowPicker(false);

    if (onChange) {
      onChange(newValue);
    }
  };

  const showPreviousYear = () => {
    const minYear = minDate ? parseInt(minDate.split("/")[1], 10) : null;
    if (!(minYear && selectedYear <= minYear)) {
      setSelectedYear((prevYear) => prevYear - 1);
    }
  };

  const showNextYear = () => {
    const maxYear = maxDate ? parseInt(maxDate.split("/")[1], 10) : null;
    if (!(maxYear && selectedYear >= maxYear)) {
      setSelectedYear((prevYear) => prevYear + 1);
    }
  };

  const toggleDatePicker = () => {
    if (inputValue) {
      const [, year] = inputValue.split("/").map(Number);
      setSelectedYear(year);
    }
    setShowPicker(true);
  };

  const minYear = minDate ? parseInt(minDate.split("/")[1], 10) : null;
  const maxYear = maxDate ? parseInt(maxDate.split("/")[1], 10) : null;
  const isPreviousDisabled = minYear && selectedYear <= minYear;
  const isNextDisabled = maxYear && selectedYear >= maxYear;

  return (
    <div className={styles["mon-container"]} ref={pickerRef}>
      <div className={`frmhead ${labelClass}`}>
        {label && <label>{label}</label>}
        {error && <div className={`error`}>{errorMessage}</div>}
      </div>
      <div>
        <div id="monthPickerInput-container">
          <Input
            type="text"
            id="monthPickerInput"
            value={inputValue}
            inputClassName={`${styles["mon-input"]} form-control`}
            placeholder="mm/yyyy"
            onClick={toggleDatePicker}
            disabled={disable}
            readOnly
            error={error}
          />
          <svg
            id={disable ? "monthPickerSvgDisabled" : "monthPickerSvg"}
            className={styles.icon}
            data-testid="monthPickerIcon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            onClick={disable ? null : toggleDatePicker}
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
            <rect x="4" y="5" width="16" height="16" rx="2"></rect>
            <line x1="16" y1="3" x2="16" y2="7"></line>
            <line x1="8" y1="3" x2="8" y2="7"></line>
            <line x1="4" y1="11" x2="20" y2="11"></line>
            <rect x="8" y="15" width="2" height="2"></rect>
          </svg>
        </div>
      </div>
      {showPicker && (
        <div className={styles["mon-wrapper"]} ref={pickerRef}>
          <div className="d-flex mon-top-class">
            <div className={styles["mon-calendar"]}>
              <div className={styles["mon-header"]}>
                <div
                  className={`${styles["mon-arrow-container"]} ${
                    isPreviousDisabled ? styles.disabled : ""
                  }`}
                  onClick={isPreviousDisabled ? null : showPreviousYear}
                >
                  <i
                    className={`${styles["mon-arrow"]} ${styles["mon-arrow-left"]}`}
                  ></i>
                </div>
                <span>{selectedYear}</span>
                <div
                  className={`${styles["mon-arrow-container"]} ${
                    isNextDisabled ? styles.disabled : ""
                  }`}
                  onClick={isNextDisabled ? null : showNextYear}
                >
                  <i
                    className={`${styles["mon-arrow"]} ${styles["mon-arrow-right"]}`}
                  ></i>
                </div>
              </div>
            </div>
          </div>
          <div className={styles["mon-ym"]}>
            {[0, 3, 6, 9].map((startIndex) => (
              <div className={styles["mon-row"]} key={startIndex}>
                {months
                  .slice(startIndex, startIndex + 3)
                  .map((month, index) => {
                    const monthIndex = startIndex + index;

                    const [maxMonth, maxYear] = maxDate
                      ? maxDate.split("/").map(Number)
                      : [null, null];
                    const [minMonth, minYear] = minDate
                      ? minDate.split("/").map(Number)
                      : [null, null];

                    const isCurrentMonth =
                      selectedYear === currentYear &&
                      monthIndex === currentMonth;
                    const isSelected =
                      selectedYear === parseInt(value.split("/")[1]) &&
                      monthIndex === selectedMonth;

                    const isDisabled =
                      (maxYear &&
                        (selectedYear > maxYear ||
                          (selectedYear === maxYear &&
                            monthIndex > maxMonth - 1))) ||
                      (minYear &&
                        (selectedYear < minYear ||
                          (selectedYear === minYear &&
                            monthIndex < minMonth - 1)));

                    return (
                      <span
                        key={monthIndex}
                        className={`${styles["mon-day"]} ${
                          isSelected
                            ? styles["mon-selected"]
                            : isCurrentMonth
                            ? styles["mon-today"]
                            : ""
                        } ${isDisabled ? styles["mon-disabled"] : ""} ${
                          isSelected ? styles["mon-selected"] : ""
                        }`}
                        onClick={() =>
                          !isDisabled ? selectMonth(monthIndex) : null
                        }
                      >
                        {month}
                      </span>
                    );
                  })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

MonthPicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  disable: PropTypes.bool,
  label: PropTypes.string,
  maxDate: PropTypes.string,
  minDate: PropTypes.string,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  labelClass: PropTypes.string,
};

export default memo(MonthPicker);



